<template>
	<div class="text-query-type-1" :class="[disabled ? 'disabled' : '', hasError ? 'has-error' : '', showSuggestions && filteredSuggestions.length > 0 ? 'active' : '']">
        <div class="editable-container"
            ref="editable"
            :contenteditable="!disabled"
            :data-placeholder="$t('fields.custom_query.placeholder')"
            @input="onInput"
            @keydown="onKeydown"
            @blur="onBlur"
        >
            <span v-html="highlightedContent"></span>
        </div>
        
        <div class="autosuggest-options" v-if="showSuggestions && filteredSuggestions.length > 0">
            <ul @click.stop>
                <li 
                    v-for="(suggestion, index) in filteredSuggestions" 
                    :key="suggestion"
                    :class="{ 'highlighted': index === highlightedIndex }"
                    @click.stop="selectSuggestion(suggestion)"
                >
                    <div class="suggestion-content">
                        <div class="name">{{ suggestion }}</div>
                    </div>
                </li>
            </ul>
        </div>

        <p v-if="hasError" class="input-error">{{ $t('fields.custom_query.errors.' + errorMessage) }}</p>
        <p v-if="!hasError" class="input-description">{{ $t('fields.custom_query.description') }}</p>
	</div>
</template>

<script>
import { debounce } from 'lodash';
import { fetchSuggestions } from '@/services/freeApi';

export default {
	name: 'CustomTextQuery',
	props: {
		modelValue: {
			type: String,
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		},
		showLabel: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			rawContent: this.modelValue || '',
			suggestions: [],
			showSuggestions: false,
			highlightedIndex: -1,
			hasError: false,
			errorMessage: '',
			wordBeingTyped: '',
			cursorPosition: 0,
			wordStart: 0
		};
	},
	computed: {
		filteredSuggestions() {
			return this.suggestions;
		},
		highlightedContent() {
			const escapeHtml = (str) =>
				str
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;');
			
			let escaped = escapeHtml(this.rawContent);

			const orRegex = /(\|\||\bOR\b)/g;
			escaped = escaped.replace(orRegex, '<span class="operator-or">$1</span>');

            const andRegex = /(&amp;&amp;|&&|\bAND\b)/g;
			escaped = escaped.replace(andRegex, '<span class="operator-and">$1</span>');

            const leftBracketRegex = /(\(|\[)/g;
            escaped = escaped.replace(leftBracketRegex, '<span class="bracket-left">$1</span>');

            const rightBracketRegex = /(\)|\])/g;
            escaped = escaped.replace(rightBracketRegex, '<span class="bracket-right">$1</span>');

			const commasRegex = /(,)/g;
			escaped = escaped.replace(commasRegex, '<span class="comma">$1</span>');
			
			return escaped;
		}
	},
	methods: {
        setError(message) {
			this.hasError = true;
			this.errorMessage = message;
		},
		clearError() {
			this.hasError = false;
			this.errorMessage = '';
		},
		onInput(event) {
			let content = event.target.innerText.replace(/\n/g, ' ');
			if (content !== event.target.innerText) {
				event.target.innerText = content;
			}
			
			this.rawContent = content;
			this.validateInput(content);
			this.processContent();

			const currentPosition = this.getCaretPosition();
			this.cursorPosition = currentPosition;
			const textBeforeCursor = this.rawContent.substring(0, currentPosition);
			const words = textBeforeCursor.split(/[\s(),&|]+/);
			this.wordBeingTyped = words[words.length - 1];
			this.wordStart = textBeforeCursor.length - this.wordBeingTyped.length;
			
			if (this.wordBeingTyped.length > 1) {
				this.debouncedGetSuggestions(this.wordBeingTyped);
				this.showSuggestions = true;
			} else {
				this.showSuggestions = false;
			}

			this.$emit('update:modelValue', this.rawContent);
		},
		onKeydown(event) {
			if (event.key === 'Enter') {
				event.preventDefault();
			}
			
			if (this.showSuggestions && this.filteredSuggestions.length > 0) {
				if (event.key === 'ArrowDown') {
					event.preventDefault();
					this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredSuggestions.length;
				} else if (event.key === 'ArrowUp') {
					event.preventDefault();
					this.highlightedIndex = this.highlightedIndex <= 0 ? this.filteredSuggestions.length - 1 : this.highlightedIndex - 1;
				} else if (event.key === 'Escape') {
					this.showSuggestions = false;
				} else if (event.key === 'Enter' && this.highlightedIndex >= 0) {
                    this.selectSuggestion(this.filteredSuggestions[this.highlightedIndex]);
                }
			}
		},
		onBlur() {
			setTimeout(() => {
				this.showSuggestions = false;
			}, 200);
			this.validateInput(this.rawContent);
		},
		saveCaretPosition(el) {
			let caretOffset = 0;
			const selection = window.getSelection();
			if (selection && selection.rangeCount) {
				const range = selection.getRangeAt(0);
				const markerEl = document.createElement("span");
				markerEl.textContent = "__CARET__";
				range.insertNode(markerEl);
				caretOffset = el.innerText.indexOf("__CARET__");
				markerEl.remove();
			}
			return caretOffset;
		},
		restoreCaretPosition(el, offset) {
			let currentOffset = 0;
			const range = document.createRange();
			let found = false;
			const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
			let node;
			while (!found && (node = walker.nextNode())) {
				const nodeLength = node.textContent.length;
				if (currentOffset + nodeLength >= offset) {
					range.setStart(node, offset - currentOffset);
					range.collapse(true);
					found = true;
					break;
				}
				currentOffset += nodeLength;
			}
			if (found) {
				const sel = window.getSelection();
				sel.removeAllRanges();
				sel.addRange(range);
			}
		},
		getCaretPosition() {
			const selection = window.getSelection();
			if (selection.rangeCount > 0) {
				const range = selection.getRangeAt(0);
				const preCaretRange = range.cloneRange();
				preCaretRange.selectNodeContents(this.$refs.editable);
				preCaretRange.setEnd(range.endContainer, range.endOffset);
				return preCaretRange.toString().length;
			}
			return 0;
		},
		validateInput(text) {
			let stack = [];
			let valid = true;
			
			for (let i = 0; i < text.length; i++) {
				if (text[i] === '(') {
					stack.push('(');
				} else if (text[i] === ')') {
					if (stack.length === 0) {
						valid = false;
						break;
					}
					stack.pop();
				}
			}
			
			if (!valid || stack.length > 0) {
				this.setError('brackets');
				return false;
			}

			if (/[&|]\s*$/.test(text)) {
				this.setError('incomplete_operator');
				return false;
			}

			this.clearError();
			return true;
		},
		async getSuggestions(query) {
			if (query.length < 2) return;
			
			try {
				const response = await fetchSuggestions({
					type: 'text',
					q: query
				});
				this.suggestions = response.data.suggest.map(s => s.term);
			} catch (error) {
				this.suggestions = [];
			}
		},
		selectSuggestion(suggestion) {
			const beforeWord = this.rawContent.substring(0, this.wordStart);
			const afterWord = this.rawContent.substring(this.cursorPosition);
			this.rawContent = beforeWord + suggestion + afterWord;

			if (this.$refs.editable) {
				this.$refs.editable.innerText = this.rawContent;
			}

			this.$nextTick(() => {
				this.setCaretPosition(this.wordStart + suggestion.length);
			});
			
			this.showSuggestions = false;
			this.$emit('update:modelValue', this.rawContent);
		},
		setCaretPosition(position) {
			const el = this.$refs.editable;
			if (!el) return;
			
			const selection = window.getSelection();
			const range = document.createRange();

			let currentPos = 0;
			let targetNode = null;
			let targetOffset = 0;
			
			const traverse = (node) => {
				if (node.nodeType === Node.TEXT_NODE) {
					const textLength = node.textContent.length;
					if (currentPos + textLength >= position) {
						targetNode = node;
						targetOffset = position - currentPos;
						return true;
					}
					currentPos += textLength;
				} else {
					for (let i = 0; i < node.childNodes.length; i++) {
						if (traverse(node.childNodes[i])) {
							return true;
						}
					}
				}
				return false;
			};
			
			traverse(el);
			
			if (targetNode) {
				range.setStart(targetNode, targetOffset);
				range.collapse(true);
				selection.removeAllRanges();
				selection.addRange(range);
			}
		},
		processContentInternal(cursor = true) {
			const editableEl = this.$refs.editable;
			if (!editableEl) return;
			let caret = false;

			if (cursor) {
				caret = this.saveCaretPosition(editableEl);
			}
			
			editableEl.innerHTML = this.highlightedContent;
			
			if (cursor) {
				this.restoreCaretPosition(editableEl, caret);
			}
		}
	},
	watch: {
		modelValue(newVal) {
			if (newVal !== this.rawContent) {
				const cleanedValue = newVal.replace(/\n/g, ' ');
				this.rawContent = cleanedValue;
				this.processContentInternal(false);
			}

            this.validateInput(newVal);
		}
	},
	mounted() {
		this.processContent = debounce(this.processContentInternal.bind(this), 300);
		this.debouncedGetSuggestions = debounce(this.getSuggestions, 300);
		
		if (this.$refs.editable) {
			this.rawContent = this.rawContent.replace(/\n/g, ' ');
			this.$refs.editable.innerHTML = this.highlightedContent;
			this.validateInput(this.rawContent);
		}
	}
};
</script>